import { configureStore, ThunkAction, Action } from '@reduxjs/toolkit';
// Import combineReducers when needed for code splitting
// import { combineReducers } from '@reduxjs/toolkit';

// Import reducers
import announcementsReducer from './slices/announcementsSlice';
import contactsReducer from './slices/contactsSlice';
import resourcesReducer from './slices/resourcesSlice';
import accessManagementReducer from './slices/accessManagementSlice';
import productHierarchyReducer from './slices/productHierarchySlice';
import productDetailsReducer from './slices/productDetailsSlice';
import userMetricsReducer from 'src/store/slices/userMetricsSlice';
import userPreferencesReducer from './slices/userPreferencesSlice';
import attributeReducer from 'src/store/slices/attributesSlice';
import scenarioSnapshotsReducer from './slices/scenarioSnapshotsSlice';
import reportsConfigReducer from './slices/reportsConfigSlice';
import commonReducer from './slices/commonSlice';
import ganttChartReducer from './slices/ganttChartSlice';
import userInfoCacheReducer from './slices/userInfoCacheSlice';
import changeHistoryReducer from './slices/changeHistorySlice';

// Note: configureStore automatically uses combineReducers under the hood when you pass an object
// As the application grows, consider using explicit code splitting with lazy loading:
//
// Example for future code splitting:
// const rootReducer = combineReducers({
//   announcements: announcementsReducer,
//   // Feature slices can be imported dynamically as the app grows
//   // users: lazy(() => import('./slices/usersSlice')).then(module => module.default),
//   // transactions: lazy(() => import('./slices/transactionsSlice')).then(module => module.default),
// });

// Create the store with redux toolkit's more efficient approach
export const store = configureStore({
  reducer: {
    announcements: announcementsReducer,
    contacts: contactsReducer,
    resources: resourcesReducer,
    accessManagement: accessManagementReducer,
    productHierarchy: productHierarchyReducer,
    productDetails: productDetailsReducer,
    userMetrics: userMetricsReducer,
    attributes: attributeReducer,
    userPreferences: userPreferencesReducer,
    scenarioSnapshots: scenarioSnapshotsReducer,
    reportsConfig: reportsConfigReducer,
    common: commonReducer,
    ganttChart: ganttChartReducer,
    userInfoCache: userInfoCacheReducer,
    changeHistory: changeHistoryReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Disable serializability check for report data to avoid performance warnings
      // Report data can contain 100K+ rows which causes the middleware to exceed
      // the 32ms warning threshold during development. This check is already
      // disabled in production builds.
      serializableCheck: {
        // Ignore specific state paths that contain large data arrays
        ignoredPaths: ['reportsConfig.reportData.data'],
        // Ignore actions that contain large data payloads
        ignoredActions: ['reportsConfig/fetchReportData/fulfilled'],
      },
      // Also configure immutability check for large datasets
      immutableCheck: {
        // Ignore the same paths for immutability checks
        ignoredPaths: ['reportsConfig.reportData.data'],
      },
    }),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
